import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CallIcon from "@mui/icons-material/Call";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import SpeedIcon from "@mui/icons-material/Speed";
import TimerIcon from "@mui/icons-material/Timer";
import { ResponsiveLine } from "@nivo/line";
import { useDispatch, useSelector } from "react-redux";
import { getCarrierMetrics } from "../../redux/actions/adminPortal_dashboardAction";

const METRIC_INFO = {
  asr: {
    title: "ASR (Answer-Seizure Ratio)",
    description:
      "Percentage of calls that were actually answered and had talk time. Calculated as: (Answered Calls / Total Calls) x 100. A low ASR indicates routing issues, carrier problems, or invalid numbers.",
    thresholds: "Green: >70% | Yellow: 50-70% | Red: <50%",
  },
  acd: {
    title: "ACD (Average Call Duration)",
    description:
      "Average duration of answered calls in seconds. Calculated as the mean of billsec for all calls where billsec > 0. Low ACD combined with low ASR often signals routing or quality problems.",
    thresholds: "Green: >180s | Yellow: 120-180s | Red: <120s",
  },
  asa: {
    title: "ASA (Average Speed of Answer)",
    description:
      "Average seconds between when the call was placed (start_at) and when it was answered (answer_at), across answered calls only. Lower is better — long ASA suggests slow carrier routing or buyers not picking up promptly. Note: this is ASA, not PDD — true Post-Dial-Delay needs SIP 18x progress timing which is not captured in this system today.",
    thresholds: "Green: <8s | Yellow: 8-15s | Red: >15s",
  },
  totalCalls: {
    title: "Total Calls",
    description:
      "Total number of call attempts for the selected date/carrier, excluding suspended DIDs. Includes answered, missed, and failed calls.",
  },
  answeredCalls: {
    title: "Answered Calls",
    description:
      "Number of calls where billsec > 0, meaning the call was connected and had actual talk time between parties.",
  },
};

const getAsrColor = (asr) => {
  if (asr > 70) return "#2e7d32";
  if (asr >= 50) return "#f59e0b";
  return "#dc2626";
};

const getAcdColor = (acd) => {
  if (acd > 180) return "#2e7d32";
  if (acd >= 120) return "#f59e0b";
  return "#dc2626";
};

// ASA color logic is INVERTED vs ACD — for answer-speed, smaller is better.
// Industry-standard contact-center thresholds: <8s great, 8-15s acceptable,
// >15s poor. A 0 value means no answered calls in the slice — render neutral
// grey so it doesn't read as a misleading "perfect score".
const getAsaColor = (asa, hasAnswers) => {
  if (!hasAnswers) return "#9e9e9e";
  if (asa < 8) return "#2e7d32";
  if (asa <= 15) return "#f59e0b";
  return "#dc2626";
};

const InfoIcon = ({ metricKey }) => (
  <Tooltip
    title={
      <Box sx={{ p: 0.5 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#fff" }}>
          {METRIC_INFO[metricKey].title}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: "#e0e0e0" }}>
          {METRIC_INFO[metricKey].description}
        </Typography>
        {METRIC_INFO[metricKey].thresholds && (
          <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: "#b0bec5" }}>
            {METRIC_INFO[metricKey].thresholds}
          </Typography>
        )}
      </Box>
    }
    arrow
    placement="top"
  >
    <InfoOutlinedIcon sx={{ fontSize: 14, color: "#9e9e9e", cursor: "help", ml: 0.5 }} />
  </Tooltip>
);

function CarrierMetricsChart({ colorThem }) {
  const isDark = colorThem === "theme_white";
  const dispatch = useDispatch();
  const { loading, data } = useSelector((state) => state.carrierMetrics);
  const [selectedCarrier, setSelectedCarrier] = useState("");

  // Fetch last 7 days on mount
  useEffect(() => {
    const endDate = new Date().toISOString().split("T")[0];
    const startDate = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    dispatch(getCarrierMetrics(startDate, endDate));
  }, [dispatch]);

  // Extract unique carriers from data
  const carriers = useMemo(() => {
    if (!data?.data) return [];
    const set = new Set(data.data.map((d) => d.carrier).filter(Boolean));
    return [...set].sort();
  }, [data]);

  // Filter data by selected carrier
  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    if (!selectedCarrier) return data.data;
    return data.data.filter((d) => d.carrier === selectedCarrier);
  }, [data, selectedCarrier]);

  // Aggregate by date for stat cards (latest date) and chart
  const { latestStats, chartData } = useMemo(() => {
    if (!filteredData.length)
      return {
        latestStats: { total_calls: 0, answered_calls: 0, asr: 0, acd: 0, asa: 0 },
        chartData: [],
      };

    // Group by date. We reconstruct the per-date sums of billsec and
    // answer-delay seconds by multiplying ACD/ASA by answered_calls — the
    // backend returns averages per (date, carrier), and "All Carriers" =
    // sum of carrier rows for the day, weighted by their answered counts.
    const byDate = {};
    filteredData.forEach((d) => {
      if (!byDate[d.date])
        byDate[d.date] = { total: 0, answered: 0, totalBillsec: 0, totalAnswerSec: 0 };
      byDate[d.date].total += d.total_calls;
      byDate[d.date].answered += d.answered_calls;
      byDate[d.date].totalBillsec += d.acd * d.answered_calls;
      byDate[d.date].totalAnswerSec += (d.asa || 0) * d.answered_calls;
    });

    const dates = Object.keys(byDate).sort();
    const latestDate = dates[dates.length - 1];
    const latest = byDate[latestDate];
    const latestAsr = latest.total > 0 ? Math.round((latest.answered / latest.total) * 10000) / 100 : 0;
    const latestAcd = latest.answered > 0 ? Math.round((latest.totalBillsec / latest.answered) * 100) / 100 : 0;
    const latestAsa = latest.answered > 0 ? Math.round((latest.totalAnswerSec / latest.answered) * 100) / 100 : 0;

    const asrLine = { id: "ASR (%)", data: [] };
    const acdLine = { id: "ACD (sec)", data: [] };
    const asaLine = { id: "ASA (sec)", data: [] };

    dates.forEach((date) => {
      const d = byDate[date];
      const formattedDate = new Date(date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      });
      const asr = d.total > 0 ? Math.round((d.answered / d.total) * 10000) / 100 : 0;
      const acd = d.answered > 0 ? Math.round((d.totalBillsec / d.answered) * 100) / 100 : 0;
      const asa = d.answered > 0 ? Math.round((d.totalAnswerSec / d.answered) * 100) / 100 : 0;
      asrLine.data.push({ x: formattedDate, y: asr });
      acdLine.data.push({ x: formattedDate, y: acd });
      asaLine.data.push({ x: formattedDate, y: asa });
    });

    return {
      latestStats: {
        total_calls: latest.total,
        answered_calls: latest.answered,
        asr: latestAsr,
        acd: latestAcd,
        asa: latestAsa,
      },
      chartData: [asrLine, acdLine, asaLine],
    };
  }, [filteredData]);

  const cardTextColor = isDark ? "#e0e0e0" : "#1f2937";
  const cardSubColor = isDark ? "#8899aa" : "#666";
  const cardBg = isDark ? "#1e2a3a" : "#fff";

  const statCards = [
    {
      title: "Total Calls",
      value: latestStats.total_calls,
      icon: CallIcon,
      accent: "#1976d2",
      infoKey: "totalCalls",
    },
    {
      title: "Answered Calls",
      value: latestStats.answered_calls,
      icon: CallReceivedIcon,
      accent: "#2e7d32",
      infoKey: "answeredCalls",
    },
    {
      title: "ASR",
      value: `${latestStats.asr}%`,
      icon: SpeedIcon,
      accent: getAsrColor(latestStats.asr),
      infoKey: "asr",
    },
    {
      title: "ACD",
      value: `${latestStats.acd}s`,
      icon: TimerIcon,
      accent: getAcdColor(latestStats.acd),
      infoKey: "acd",
    },
    {
      title: "ASA",
      value: `${latestStats.asa}s`,
      icon: TimerIcon,
      accent: getAsaColor(latestStats.asa, latestStats.answered_calls > 0),
      infoKey: "asa",
    },
  ];

  if (loading) {
    return (
      <Box>
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" width={220} height={90} sx={{ borderRadius: "12px", flex: "1 1 200px" }} />
          ))}
        </Stack>
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: "12px" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Carrier filter */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by Carrier</InputLabel>
          <Select
            value={selectedCarrier}
            label="Filter by Carrier"
            onChange={(e) => setSelectedCarrier(e.target.value)}
          >
            <MenuItem value="">All Carriers</MenuItem>
            {carriers.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" sx={{ color: cardSubColor }}>
          Showing last 7 days
        </Typography>
      </Stack>

      {/* Stat Cards */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        {statCards.map((card) => (
          <Card
            key={card.title}
            sx={{
              flex: "1 1 200px",
              minWidth: 180,
              p: 2,
              borderRadius: "12px",
              backgroundColor: cardBg,
              border: `1px solid ${card.accent}22`,
              boxShadow: `0 4px 16px ${card.accent}15`,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "5px",
                height: "100%",
                backgroundColor: card.accent,
                borderTopLeftRadius: "12px",
                borderBottomLeftRadius: "12px",
              },
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Stack direction="row" alignItems="center">
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: card.accent,
                      letterSpacing: 0.5,
                    }}
                  >
                    {card.title}
                  </Typography>
                  <InfoIcon metricKey={card.infoKey} />
                </Stack>
                <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, color: cardTextColor, mt: 0.5 }}>
                  {card.value}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: `${card.accent}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px solid ${card.accent}30`,
                }}
              >
                <card.icon sx={{ fontSize: 22, color: card.accent }} />
              </Box>
            </Box>
          </Card>
        ))}
      </Stack>

      {/* Trend Chart */}
      {chartData.length > 0 && chartData[0].data.length > 0 ? (
        <Box sx={{ height: "45vh", minHeight: 300 }}>
          <ResponsiveLine
            data={chartData}
            margin={{ top: 30, right: 120, bottom: 50, left: 60 }}
            xScale={{ type: "point" }}
            yScale={{ type: "linear", min: "auto", max: "auto", stacked: false }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              legend: "Value",
              legendOffset: -45,
              legendPosition: "middle",
            }}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: "Date",
              legendOffset: 36,
              legendPosition: "middle",
            }}
            colors={["#1976d2", "#f59e0b"]}
            pointSize={8}
            pointColor={{ theme: "background" }}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            enableCrosshair={true}
            useMesh={true}
            theme={{
              axis: {
                ticks: { text: { fill: isDark ? "#a0b4c8" : "#555" } },
                legend: { text: { fill: isDark ? "#a0b4c8" : "#555" } },
              },
              grid: { line: { stroke: isDark ? "#2a3a4a" : "#e0e0e0" } },
              crosshair: { line: { stroke: isDark ? "#a0b4c8" : "#555" } },
              legends: { text: { fill: isDark ? "#a0b4c8" : "#555" } },
              tooltip: {
                container: {
                  background: isDark ? "#1a2332" : "#fff",
                  color: isDark ? "#e0e0e0" : "#333",
                },
              },
            }}
            legends={[
              {
                anchor: "bottom-right",
                direction: "column",
                translateX: 110,
                itemWidth: 90,
                itemHeight: 20,
                symbolSize: 12,
                symbolShape: "circle",
              },
            ]}
          />
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography variant="body2" sx={{ color: cardSubColor }}>
            No carrier metrics data available for the selected period.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default CarrierMetricsChart;
